import React from "react";
import { Page, Text, View, Document, StyleSheet, Image } from "@react-pdf/renderer";

const ORIENTATION = "portrait"; // "portrait" ou "landscape"
const CHARTS_PER_PAGE = 3;
const CHART_HEIGHT = 200;

const styles = StyleSheet.create({
  page: { padding: 28, fontSize: 11, fontFamily: "Helvetica" },
  headerRow: { flexDirection: "row", alignItems: "center", marginBottom: 10 },
  headerText: { flex: 1 },
  logo: { width: 56, height: 56, objectFit: "contain", marginLeft: 8, borderRadius: 8, backgroundColor: "#fff" },

  h1: { fontSize: 18, fontWeight: 700, marginBottom: 2 },
  h2: { fontSize: 13, fontWeight: 700, marginBottom: 6 },
  subtitle: { fontSize: 10, color: "#6b7280", marginBottom: 10 },
  divider: { height: 1, backgroundColor: "#e5e7eb", marginVertical: 8 },

  card: {
    padding: 10, borderWidth: 1, borderColor: "#e5e7eb",
    borderRadius: 10, marginBottom: 12, backgroundColor: "#ffffff",
  },

  table: { borderWidth: 1, borderColor: "#e5e7eb", borderRadius: 8, overflow: "hidden" },
  tr: { flexDirection: "row" },
  th: {
    flex: 1, padding: 6, fontWeight: 700, backgroundColor: "#f8fafc",
    borderRight: 1, borderColor: "#e5e7eb",
  },
  td: { flex: 1, padding: 6, borderTop: 1, borderRight: 1, borderColor: "#e5e7eb" },
  tdRight: { textAlign: "right" },
  small: { fontSize: 9, color: "#6b7280", marginTop: 6 },

  grid2: { flexDirection: "row", columnGap: 10 },
  col: { flex: 1 },

  chart: { width: "100%", height: 210, objectFit: "contain", borderRadius: 8, marginTop: 6 },
  chartHalf: { width: "100%", height: 150, objectFit: "contain", borderRadius: 8, marginTop: 6, marginBottom: 6 },
  chartFull: { width: "100%", height: CHART_HEIGHT, objectFit: "contain", borderRadius: 10, marginTop: 6, marginBottom: 8 },

  legendCard: {
    padding: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 8,
    marginBottom: 10,
    backgroundColor: "#ffffff",
  },
  legendTitle: { fontSize: 11, fontWeight: 700, marginBottom: 6 },
  legendRow: { flexDirection: "row" },
  legendCol: { flex: 1, gap: 4 },
  legendItem: { flexDirection: "row", alignItems: "center", marginBottom: 4 },
  swatch: { width: 10, height: 10, borderRadius: 2, marginRight: 6 },
  legendText: { fontSize: 9, color: "#374151" },
  avgLine: { width: 14, height: 0, borderTopWidth: 2, marginRight: 6 },
  thSmall: { flex: 1, padding: 6, fontWeight: 700, backgroundColor: "#f8fafc",
             borderRight: 1, borderColor: "#e5e7eb", fontSize: 10 },
  tdTight: { flex: 1, padding: 6, borderTop: 1, borderRight: 1, borderColor: "#e5e7eb" },
  caption: { fontSize: 9, color: "#6b7280", marginTop: 4 },

  riiCard: { padding: 8, borderWidth: 1, borderColor: "#e5e7eb", borderRadius: 10, backgroundColor: "#fff", marginTop: 8 },
  riiItem: { marginBottom: 8 },
  riiRow: { flexDirection: "row", alignItems: "center", marginBottom: 8 },
  riiAvatar: { width: 24, height: 24, borderRadius: 6, backgroundColor: "#e5e7eb", marginRight: 8 },
  riiName: { fontSize: 11, fontWeight: 700 },
  riiPos: { fontSize: 9, color: "#374151", paddingVertical: 2, paddingHorizontal: 6, borderWidth: 1, borderColor: "#e5e7eb", borderRadius: 999, marginLeft: 6 },
  riiBarBg: { height: 6, backgroundColor: "#e5e7eb", borderRadius: 4, overflow: "hidden", marginTop: 4 },
  riiBarFill: { height: 6, backgroundColor: "#10B981" },
  riiRight: { marginLeft: "auto", alignItems: "flex-end" },
  riiPct: { fontSize: 11, fontWeight: 700 },
  riiDelta: { fontSize: 9, marginTop: 2 },

  footer: { position: "absolute", bottom: 14, left: 28, right: 28, textAlign: "center", color: "#9ca3af" },
});

const TD = ({ children, right }) => (
  <Text style={[styles.td, right && styles.tdRight]}>{children}</Text>
);
const fmt = (v) => (v == null || Number.isNaN(v) ? "—" : v);
const normalizeDataUrl = (raw) => {
  if (!raw) return null;
  const s = String(raw);
  if (s.startsWith("data:")) return s;
  if (/^https?:\/\//i.test(s)) return s;
  return `data:image/png;base64,${s}`;
};
const deltaStyle = (delta) => {
  if (!Number.isFinite(delta)) return { color: "#6b7280" };       // gris
  if (delta > 0) return { color: "#059669", fontWeight: 700 };    // vert
  if (delta < 0) return { color: "#B91C1C", fontWeight: 700 };    // rouge
  return { color: "#6b7280" };
};
const fmtDelta = (d, { percent = false, unit = "", decimals = 2 } = {}) => {
  if (!Number.isFinite(d)) return "—";
  const v = Math.abs(percent ? d * 100 : d).toFixed(decimals);
  const sign = d > 0 ? "+" : d < 0 ? "−" : "=";
  return `${sign}${v}${percent ? "%" : unit}`;
};

// petit composant valeur + delta (delta en sous-ligne)
const ValueWithDelta = ({ value, delta, percent = false, unit = "", decimals = 2 }) => (
  <View>
    <Text>{value == null ? "—" : (percent ? `${(value*100).toFixed(decimals)}%` : `${Number(value).toFixed(decimals)}${unit}`)}</Text>
    <Text style={[{ fontSize: 9 }, deltaStyle(delta)]}>{fmtDelta(delta, { percent, unit, decimals: percent ? 1 : 2 })}</Text>
  </View>
);

export default function ClubReportPdf({
  club, season, teamStats, byPosition = [], images = {}, data = {}, logoDataUrl
}) {
  const internalKeys = data.__club_internal_keys || [];
  const externalKeys = data.__club_external_keys || [];
  const seriesByMetric = data.__series || {};
  const logoSrc = normalizeDataUrl(logoDataUrl);

  const legend = data.__legend || {};
  const colorEntries = Object.entries(legend.colorMap || {});
  const topRii = Array.isArray(data.__topRii) ? data.__topRii : [];
  const lowTrain = Array.isArray(data.__lowTrain) ? data.__lowTrain : [];

  const trainabilityScore = (row) => {
    const score = Number(row?.score ?? row?.rii);
    return Number.isFinite(score) ? Math.max(0, Math.min(10, score)) : NaN;
  };

  const splitIntoColumns = (entries, cols = 2) => {
    const out = Array.from({ length: cols }, () => []);
    entries.forEach((e, i) => out[i % cols].push(e));
    return out;
  };

  const LegendBlock = ({ title }) => {
    const legend = data.__legend || {};
    // rétro-compatibilité
    const showMedian = legend.showMedian ?? false;
    const medianColor = legend.medianColor || legend.teamAvgColor || "#111827";

    // bande σ optionnelle
    const bandStroke = legend.stdBandStroke || legend.teamAvgColor || "#2563EB";
    const bandFill   = legend.stdBandFill   || "rgba(59,130,246,0.18)";

    // joueurs
    const items = [];
    if (showMedian) {
      items.push({ label: "Médiane (équipe)", type: "line", color: medianColor, dashed: false });
    } else if (legend.showTeamAvg) {
      items.push({ label: "Moyenne équipe", type: "line", color: legend.teamAvgColor || "#111827", dashed: true });
    }
    Object.entries(legend.colorMap || {}).forEach(([name, color]) =>
      items.push({ label: name, type: "swatch", color })
    );

    // rendu
    const splitIntoColumns = (entries, cols = 2) => {
      const out = Array.from({ length: cols }, () => []);
      entries.forEach((e, i) => out[i % cols].push(e));
      return out;
    };
    const cols = splitIntoColumns(items, 2);

    return (
      <View style={styles.legendCard}>
        <Text style={styles.legendTitle}>{title} — Légende</Text>

        {/* Bande σ */}
        <View style={[styles.legendItem, { marginBottom: 6 }]}>
          <View style={[styles.swatch, { backgroundColor: bandFill, borderWidth: 1, borderColor: bandStroke }]} />
          <Text style={styles.legendText}>Plage ± autour de la moyenne</Text>
        </View>

        {/* Lignes / joueurs */}
        <View style={styles.legendRow}>
          {cols.map((col, idx) => (
            <View key={idx} style={styles.legendCol}>
              {col.map((it, j) => (
                <View key={`${idx}-${j}`} style={styles.legendItem}>
                  {it.type === "swatch" ? (
                    <View style={[styles.swatch, { backgroundColor: it.color }]} />
                  ) : (
                    <View style={[styles.avgLine, { borderTopColor: it.color, borderStyle: it.dashed ? "dashed" : "solid" }]} />
                  )}
                  <Text style={styles.legendText}>{it.label}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      </View>
    );
  };

  const radarKeys = Object.keys(images || {}).filter(k => k.startsWith("radar."));
  const chunk = (arr, size) => { const out=[]; for (let i=0;i<arr.length;i+=size) out.push(arr.slice(i,i+size)); return out; };

  const RadarsSection = () => {
    if (!radarKeys.length) return null;
    const pairs = chunk(radarKeys, 2);
    return (
      <View style={styles.card} wrap={false}>
        <Text style={styles.h2}>Radars par poste</Text>

        {pairs.map((pair, rowIdx) => (
          <View key={`rad-row-${rowIdx}`} style={[styles.grid2, { marginBottom: 8 }]} wrap={false}>
            {pair.map((k) => (
              <View key={k} style={styles.col} wrap={false}>
                <Image src={images[k]} style={styles.chartHalf} />
                <Text style={styles.small}>
                  {k.replace(/^radar\./, "").replace(/\./g, " / ")}
                </Text>
              </View>
            ))}
            {/* Si nombre impair, on ajoute une colonne vide pour garder la grille propre */}
            {pair.length === 1 && <View style={styles.col} />}
          </View>
        ))}
      </View>
    );
  };

  const numOrDash = (v, decimals = 2) => {
    if (v == null || Number.isNaN(v)) return "—";
    return Number(v).toFixed(decimals);
  };

  const KPIBlock = (
    <View style={styles.card}>
      <Text style={styles.h2}>KPI moyens (équipe)</Text>
      <View style={styles.table}>
        <View style={styles.tr}>
          <Text style={styles.th}>Metric</Text>
          <Text style={styles.th}>Valeur</Text>
          <Text style={styles.th}>Δ vs prev.</Text>
        </View>

        {[
          // label, valeur, delta, options d'affichage
          ["Intensity (PT)",          teamStats?.avgIntensityPT,   teamStats?.deltas?.avgIntensityPT,   { unit: "",      percent: false, decimals: 2 }],
          ["Mechanical (PT)",         teamStats?.avgMechPT,        teamStats?.deltas?.avgMechPT,        { unit: "",      percent: false, decimals: 2 }],
          ["RII",                     teamStats?.avgRII,           teamStats?.deltas?.avgRII,           { percent: true,              decimals: 1 }],
          ["MLI",                     teamStats?.avgMLI,           teamStats?.deltas?.avgMLI,           { percent: true,              decimals: 1 }],
          ["Fatigue",                 teamStats?.avgFatigue,       teamStats?.deltas?.avgFatigue,       { percent: true,              decimals: 1 }],
          ["Acc max (m/s²)",          teamStats?.avgAcc,           teamStats?.deltas?.avgAcc,           { unit: " m/s²", percent: false, decimals: 2 }],
          ["Dec max (m/s²)",          teamStats?.avgDec,           teamStats?.deltas?.avgDec,           { unit: " m/s²", percent: false, decimals: 2 }],
          ["Sprint max (km/h)",       teamStats?.avgSprint,        teamStats?.deltas?.avgSprint,        { unit: " km/h", percent: false, decimals: 2 }],
          ["Global distance (km)",    teamStats?.sumDistanceKm,    teamStats?.deltas?.sumDistanceKm,    { unit: " km",   percent: false, decimals: 2 }],
          // ⬇️ ici on force 0 décimale
          ["Total COD",               teamStats?.sumCod,           teamStats?.deltas?.sumCod,           { unit: "",      percent: false, decimals: 0 }],
        ].map(([label, val, dlt, opt], i) => (
          <View key={i} style={styles.tr}>
            <TD>{label}</TD>

            {/* Valeur */}
            <TD right>
              {opt.percent
                ? (val == null
                    ? "—"
                    : `${(val * 100).toFixed(opt.decimals ?? 1)}%`)
                : val == null || Number.isNaN(val)
                  ? "—"
                  : `${Number(val).toFixed(opt.decimals ?? 2)}${opt.unit ?? ""}`}
            </TD>

            {/* Delta */}
            <TD right>
              <Text
                style={deltaStyle(dlt)}
              >
                {fmtDelta(dlt, {
                  percent: !!opt.percent,
                  unit: opt.unit,
                  decimals: opt.percent ? 1 : 2,
                })}
              </Text>
            </TD>
          </View>
        ))}
      </View>
    </View>
  );

  const PositionBlock = (
    <View style={styles.card}>
      <Text style={styles.h2}>Moyennes par poste</Text>
      <View style={styles.table}>
        <View style={styles.tr}>
          <Text style={styles.thSmall}>Poste</Text>
          <Text style={styles.thSmall}>#</Text>
          <Text style={styles.thSmall}>Intensity</Text>
          <Text style={styles.thSmall}>Mechanical</Text>
          <Text style={styles.thSmall}>RII</Text>
          <Text style={styles.thSmall}>MLI</Text>
          <Text style={styles.thSmall}>Fatigue</Text>
          <Text style={styles.thSmall}>Acc</Text>
          <Text style={styles.thSmall}>Dec</Text>
          <Text style={styles.thSmall}>Sprint</Text>
        </View>

        {byPosition.map((r, i)=>(
          <View key={i} style={styles.tr}>
            <TD>{r.pos}</TD>
            <TD right>{fmt(r.n)}</TD>

            <View style={styles.tdTight}><ValueWithDelta value={r.intensityPT} delta={r.d_intensityPT} /></View>
            <View style={styles.tdTight}><ValueWithDelta value={r.mechanicalPT} delta={r.d_mechanicalPT} /></View>

            <View style={styles.tdTight}><ValueWithDelta value={r.rii}     delta={r.d_rii}     percent /></View>
            <View style={styles.tdTight}><ValueWithDelta value={r.mli}     delta={r.d_mli}     percent /></View>
            <View style={styles.tdTight}><ValueWithDelta value={r.fatigue} delta={r.d_fatigue} percent /></View>

            <View style={styles.tdTight}><ValueWithDelta value={r.acc}    delta={r.d_acc}    unit=" m/s²" /></View>
            <View style={styles.tdTight}><ValueWithDelta value={r.dec}    delta={r.d_dec}    unit=" m/s²" /></View>
            <View style={styles.tdTight}><ValueWithDelta value={r.sprint} delta={r.d_sprint} unit=" km/h" /></View>
          </View>
        ))}
      </View>
      <Text style={styles.caption}>Les Δ comparent à la période précédente (colonnes “prev” calculées côté app).</Text>
    </View>
  );

  const TrainabilityBlock = () => {
    if (!lowTrain.length && !topRii.length) return null;

    return (
      <View style={styles.card} wrap={false}>
        <Text style={styles.h2}>Trainability — performance scores</Text>

        <View style={styles.grid2}>
          <View style={styles.col}>
            <Text style={styles.small}>Lowest performance scores</Text>
            <View style={styles.riiCard}>
              {lowTrain.length ? (
                lowTrain.map((p, i) => {
                  const score = trainabilityScore(p);
                  const pct = Number.isFinite(score) ? score * 10 : 0;
                  const deltaTxt = fmtDelta(p.delta, { unit: " pts", decimals: 1 });

                  return (
                    <View key={p.id || `${p.name}-low-${i}`} style={styles.riiItem}>
                      <View style={styles.riiRow}>
                        {p.avatar ? (
                          <Image src={normalizeDataUrl(p.avatar)} style={styles.riiAvatar} />
                        ) : (
                          <View style={styles.riiAvatar} />
                        )}

                        <Text style={styles.riiName}>{`${i + 1}. ${p.name || "N/A"}`}</Text>
                        <Text style={styles.riiPos}>{p.position || "N/A"}</Text>

                        <View style={styles.riiRight}>
                          <Text style={styles.riiPct}>{Number.isFinite(score) ? `${score.toFixed(1)}/10` : "—"}</Text>
                          <Text style={[styles.riiDelta, deltaStyle(p.delta)]}>{deltaTxt}</Text>
                        </View>
                      </View>

                      <View style={styles.riiBarBg}>
                        <View style={[styles.riiBarFill, { width: `${pct}%` }]} />
                      </View>
                    </View>
                  );
                })
              ) : (
                <Text style={styles.small}>No trainability score available.</Text>
              )}
            </View>
          </View>

          <View style={styles.col}>
            <Text style={styles.small}>Best performance scores</Text>
            <View style={styles.riiCard}>
              {topRii.length ? (
                topRii.map((p, i) => {
                  const score = trainabilityScore(p);
                  const pct = Number.isFinite(score) ? score * 10 : 0;
                  const deltaTxt = fmtDelta(p.delta, { unit: " pts", decimals: 1 });

                  return (
                    <View key={p.id || `${p.name}-best-${i}`} style={styles.riiItem}>
                      <View style={styles.riiRow}>
                        {p.avatar ? (
                          <Image src={normalizeDataUrl(p.avatar)} style={styles.riiAvatar} />
                        ) : (
                          <View style={styles.riiAvatar} />
                        )}

                        <Text style={styles.riiName}>{`${i + 1}. ${p.name || "N/A"}`}</Text>
                        <Text style={styles.riiPos}>{p.position || "N/A"}</Text>

                        <View style={styles.riiRight}>
                          <Text style={styles.riiPct}>{Number.isFinite(score) ? `${score.toFixed(1)}/10` : "—"}</Text>
                          <Text style={[styles.riiDelta, deltaStyle(p.delta)]}>{deltaTxt}</Text>
                        </View>
                      </View>

                      <View style={styles.riiBarBg}>
                        <View style={[styles.riiBarFill, { width: `${pct}%` }]} />
                      </View>
                    </View>
                  );
                })
              ) : (
                <Text style={styles.small}>No trainability score available.</Text>
              )}
            </View>
          </View>
        </View>
      </View>
    );
  };



  const METRIC_LABELS = {
    "internal.mechanical_fatigue": "Mechanical Fatigue (%)",
    "internal.intensity_index": "Intensity (PT)",
    "internal.mechanical_load_index": "Mechanical Load (PT)",
    "internal.power": "Max horizontal mechanical power (W/kg)",
    "external.accelerations": "Accelerations max (m/s²)",
    "external.decelerations": "Decelerations max (m/s²)",
    "external.sprint_max": "Speed max (km/h)",
    "external.global_distance": "Total Distance (km)",
  };
  const labelFor = (metricKey) => METRIC_LABELS[metricKey] || metricKey;



  function MetricMiniTable({ metricKey }) {
    const rows = seriesByMetric[metricKey] || [];
    if (!rows.length) return null;

    const last = rows[rows.length - 1];
    const players = Object.keys(last)
      .filter(k => k !== "date" && k !== "__avg")
      .sort((a,b)=> a.localeCompare(b));

    const limited = players.slice(0, 10);

    return (
      <View style={{ marginTop: 6 }}>
        <Text style={styles.small}>
          Dernière date: {last.date ?? "—"} · Team avg: {fmt(last.__avg)}
        </Text>
        <View style={[styles.table, { marginTop: 4 }]}>
          <View style={styles.tr}>
            <Text style={[styles.th, { flex: 2 }]}>Joueur</Text>
            <Text style={[styles.th, { flex: 1 }]}>Valeur</Text>
          </View>
          {limited.map((p) => (
            <View key={p} style={styles.tr}>
              <TD>{p}</TD>
              <TD right>{fmt(last[p])}</TD>
            </View>
          ))}
        </View>
        {players.length > limited.length && (
          <Text style={styles.small}>… {players.length - limited.length} joueurs supplémentaires masqués pour compacité.</Text>
        )}
      </View>
    );
  }

  const ChartsGroup = ({ title, keys }) => {
    if (!keys.length) return null;

    const leftKeys  = keys.filter((_,i)=> i % 2 === 0);
    const rightKeys = keys.filter((_,i)=> i % 2 === 1);

    const Column = ({ sideKeys }) => (
      <View style={styles.col}>
        {sideKeys.map((fullKey) => {
          const img = images[fullKey];
          if (!img) return null;
          const metricKey = fullKey.split(".").slice(1).join(".");
          return (
            <View key={fullKey} wrap={false}>
              <Text style={styles.small}>{labelFor(metricKey)}</Text>
              <Image src={img} style={styles.chartHalf} />
              <MetricMiniTable metricKey={metricKey} />
            </View>
          );
        })}
      </View>
    );

    return (
      <View style={styles.card} wrap={false}>
        <Text style={styles.h2}>{title}</Text>
        <View style={styles.grid2}>
          <Column sideKeys={leftKeys} />
          <Column sideKeys={rightKeys} />
        </View>
      </View>
    );
  };

  const FullWidthChartsGroup = ({ title, keys }) => {
    if (!keys.length) return null;
    return (
      <View style={styles.card} wrap={false}>
        <Text style={styles.h2}>{title}</Text>
        {keys.map((fullKey) => {
          const img = images[fullKey];
          if (!img) return null;
          const metricKey = fullKey.split(".").slice(1).join(".");
          return (
            <View key={fullKey} wrap={false}>
              <Text style={styles.small}>{labelFor(metricKey)}</Text>
              <Image src={img} style={styles.chartFull} />
              <MetricMiniTable metricKey={metricKey} />
            </View>
          );
        })}
      </View>
    );
  };

  const RadarLegendBlock = () => (
    <View style={styles.legendCard} wrap={false}>
      <Text style={styles.legendTitle}>Radars — Légende</Text>
      <View style={styles.legendItem}>
        <View style={[styles.swatch, { backgroundColor: "rgba(59,130,246,0.18)", borderWidth: 1, borderColor: "#2563EB" }]} />
        <Text style={styles.legendText}>Plage (min → max)</Text>
      </View>
      <View style={styles.legendItem}>
        <View style={[styles.avgLine, { borderTopColor: "#7C3AED" }]} />
        <Text style={styles.legendText}>Moyenne du poste</Text>
      </View>
      <View style={styles.legendItem}>
        <View style={[styles.avgLine, { borderTopColor: "#059669" }]} />
        <Text style={styles.legendText}>Moyenne équipe</Text>
      </View>
    </View>
  );

  return (
    <Document>
      {/* PAGE 1 — En-tête + KPI + Table postes */}
      <Page size="A4" style={styles.page}>
        <View style={styles.headerRow}>
          <View style={styles.headerText}>
            <Text style={styles.h1}>Club Report</Text>
            <Text style={styles.subtitle}>
              {club?.name || "Club"} — Season {season} — {new Date().toLocaleDateString("fr-FR")}
            </Text>
          </View>
          {logoSrc ? <Image src={logoSrc} style={styles.logo} /> : null}
        </View>

        {KPIBlock}
        <TrainabilityBlock />

        {/* Légende pour les courbes de trends */}
        <LegendBlock title="Trends" />

        <Text style={styles.footer}>Generated by PrediTwins</Text>
      </Page>

      {/* PAGE 2 — Radars par poste */}
      {radarKeys.length > 0 && (
        <Page size="A4" style={styles.page}>
          <Text style={styles.h2}>Radars by Position</Text>
          <RadarsSection />
          <RadarLegendBlock />
        </Page>
      )}

      {/* PAGE 3 — Graphiques Internal */}
      {chunk(internalKeys, CHARTS_PER_PAGE).map((keys, i) => (
        <Page key={`int-${i}`} size="A4" orientation={ORIENTATION} style={styles.page}>
          <FullWidthChartsGroup title="Trends — Internal" keys={keys} />
        </Page>
      ))}

      {/* PAGE 4 — Graphiques External */}
      {chunk(externalKeys, CHARTS_PER_PAGE).map((keys, i) => (
        <Page key={`ext-${i}`} size="A4" orientation={ORIENTATION} style={styles.page}>
          <FullWidthChartsGroup title="Trends — External" keys={keys} />
        </Page>
      ))}
    </Document>
  );
}
